import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { createOrder } from "../lib/api";
import { formatMoneyDecimal } from "../lib/categories";
import { saveOrder } from "../lib/localStore";
import SmartImg from "../components/SmartImg";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const INITIAL = {
  fullName: "",
  email: "",
  phone: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "United States",
};

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [placing, setPlacing] = useState(false);

  const tax = Math.round(subtotal * 0.08 * 100) / 100;
  const shippingFee = 0;
  const total = Math.round((subtotal + shippingFee + tax) * 100) / 100;

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;

    // Minimal soft-check on required-ish fields; app is fictional per requirements.
    const required = ["fullName", "email", "address1", "city", "state", "postalCode"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill in ${k}`);
        return;
      }
    }

    setPlacing(true);
    try {
      const order = await createOrder({
        items,
        shipping: form,
        subtotal,
        shippingFee,
        tax,
        total,
      });
      saveOrder(order);
      clear();
      navigate(`/order/${order.orderNumber}`, { state: { order } });
    } catch (err) {
      toast.error("Could not place order. Please try again.");
      setPlacing(false);
    }
  };

  if (items.length === 0 && !placing) {
    return (
      <main className="mx-auto max-w-[900px] px-5 lg:px-10 py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-display font-bold text-3xl tracking-tight">
          Your cart is empty
        </h1>
        <p className="text-neutral-600 mt-3">
          There&apos;s nothing to check out. Try adding a boat.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors"
        >
          Start shopping
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10" data-testid="checkout-page">
      <nav className="text-xs text-neutral-500 mb-3">
        <Link to="/cart" className="hover:text-neutral-900">Cart</Link>
        <span className="mx-2">/</span>
        <span className="text-neutral-900 font-medium">Checkout</span>
      </nav>

      <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter mb-8">
        Checkout
      </h1>

      <form onSubmit={placeOrder} className="grid grid-cols-12 gap-8">
        {/* Left: Shipping/contact form */}
        <div className="col-span-12 lg:col-span-7 space-y-8">
          <section className="border border-neutral-200 rounded-xl p-6 bg-white">
            <h2 className="font-display font-semibold text-lg tracking-tight mb-1">
              Shipping details
            </h2>
            <p className="text-xs text-neutral-500 mb-5">
              This is a fictional storefront. Use fake data &mdash; nothing is stored or sent.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Full name" required span={2} testId="checkout-fullname">
                <input required value={form.fullName} onChange={on("fullName")} className={inputCls} data-testid="checkout-fullname-input" />
              </Field>
              <Field label="Email" required testId="checkout-email">
                <input type="email" required value={form.email} onChange={on("email")} className={inputCls} data-testid="checkout-email-input" />
              </Field>
              <Field label="Phone" testId="checkout-phone">
                <input value={form.phone} onChange={on("phone")} className={inputCls} data-testid="checkout-phone-input" />
              </Field>
              <Field label="Address line 1" required span={2} testId="checkout-address1">
                <input required value={form.address1} onChange={on("address1")} className={inputCls} data-testid="checkout-address1-input" />
              </Field>
              <Field label="Address line 2" span={2} testId="checkout-address2">
                <input value={form.address2} onChange={on("address2")} className={inputCls} data-testid="checkout-address2-input" />
              </Field>
              <Field label="City" required testId="checkout-city">
                <input required value={form.city} onChange={on("city")} className={inputCls} data-testid="checkout-city-input" />
              </Field>
              <Field label="State / Province" required testId="checkout-state">
                <input required value={form.state} onChange={on("state")} className={inputCls} data-testid="checkout-state-input" />
              </Field>
              <Field label="Postal code" required testId="checkout-postal">
                <input required value={form.postalCode} onChange={on("postalCode")} className={inputCls} data-testid="checkout-postal-input" />
              </Field>
              <Field label="Country" testId="checkout-country">
                <input value={form.country} onChange={on("country")} className={inputCls} data-testid="checkout-country-input" />
              </Field>
            </div>
          </section>

          <section className="border border-neutral-200 rounded-xl p-6 bg-white">
            <h2 className="font-display font-semibold text-lg tracking-tight">
              Payment
            </h2>
            <div className="mt-3 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-700 leading-relaxed">
              <span className="font-medium">No payment required.</span>{" "}
              BrokeShopping.com is a fictional demo storefront &mdash; we don&apos;t collect
              credit cards, bank info, or anything monetary. Clicking &quot;Place order&quot;
              simulates a purchase and shows you a confirmation.
            </div>
          </section>
        </div>

        {/* Right: Order summary */}
        <aside className="col-span-12 lg:col-span-5">
          <div className="border border-neutral-200 rounded-xl p-6 bg-white lg:sticky lg:top-32">
            <h2 className="font-display font-bold text-xl tracking-tight">
              Order summary
            </h2>
            <ul className="mt-5 space-y-3 max-h-64 overflow-auto pr-1" data-testid="checkout-items">
              {items.map((i) => (
                <li key={i.productId} className="flex items-center gap-3">
                  <SmartImg
                    src={i.image}
                    alt={i.title}
                    className="h-14 w-14 rounded-md shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-900 truncate">
                      {i.title}
                    </div>
                    <div className="text-xs text-neutral-500">Qty {i.quantity}</div>
                  </div>
                  <div className="text-sm font-medium text-neutral-900">
                    {formatMoneyDecimal(i.price * i.quantity)}
                  </div>
                </li>
              ))}
            </ul>
            <dl className="mt-5 pt-5 border-t border-neutral-200 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-neutral-600">Subtotal</dt>
                <dd className="font-medium">{formatMoneyDecimal(subtotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-600">Shipping</dt>
                <dd className="font-medium">Free</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-600">Tax</dt>
                <dd className="font-medium">{formatMoneyDecimal(tax)}</dd>
              </div>
            </dl>
            <div className="mt-4 pt-4 border-t border-neutral-200 flex justify-between items-baseline">
              <span className="font-display font-semibold">Total</span>
              <span className="font-display font-bold text-2xl" data-testid="checkout-total">
                {formatMoneyDecimal(total)}
              </span>
            </div>
            <button
              type="submit"
              disabled={placing}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 disabled:opacity-70 transition-colors"
              data-testid="place-order-button"
            >
              {placing && <Loader2 className="animate-spin" size={16} />}
              {placing ? "Placing order…" : "Place order"}
            </button>
            <p className="mt-3 text-[11px] text-neutral-500 text-center leading-relaxed">
              By placing this fictional order, you agree that nothing will ship
              to your imaginary address.
            </p>
          </div>
        </aside>
      </form>
    </main>
  );
}

const inputCls =
  "w-full h-10 rounded-md border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10 transition-colors";

function Field({ label, required, span = 1, children, testId }) {
  return (
    <label
      className={`block ${span === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}`}
      data-testid={testId}
    >
      <span className="block text-xs font-medium text-neutral-700 mb-1.5">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
    </label>
  );
}
